import { useAuth } from "@/contexts/AuthContext";

/** Feature keys we gate behind paid tiers. */
export type FeatureKey =
  | "bulk"
  | "team"
  | "client-portal"
  | "api-access"
  | "white-labeling";

interface FeatureMeta {
  /** Minimum tier required (logical) */
  requiredTier: "agency" | "enterprise";
  /** Display name shown in the upgrade modal */
  title: string;
  /** Short pitch shown to the user */
  description: string;
  /** Concrete benefits highlighted in the modal */
  benefits: string[];
  /** Plan label for the CTA — "Agency" or "Enterprise" */
  planLabel: string;
}

export const FEATURE_META: Record<FeatureKey, FeatureMeta> = {
  bulk: {
    requiredTier: "agency",
    title: "Bulk Proposal Generation",
    planLabel: "Agency",
    description:
      "Generate up to 25 AI-powered proposals at once from a CSV or batch form — perfect for agencies serving multiple clients.",
    benefits: [
      "Create up to 25 proposals in a single run",
      "Same risk analysis & cost calculator as single proposals",
      "Auto-saves each proposal to your history",
      "Massive time-saver for monthly client cycles",
    ],
  },
  team: {
    requiredTier: "agency",
    title: "Team Management",
    planLabel: "Agency",
    description:
      "Invite up to 5 team members so your staff can create proposals under your account, with shared history and branding.",
    benefits: [
      "Up to 5 team member seats",
      "Shared proposal history across the team",
      "Centralized billing — one subscription",
      "Owner controls who can create proposals",
    ],
  },
  "client-portal": {
    requiredTier: "agency",
    title: "Client Portal Sharing",
    planLabel: "Agency",
    description:
      "Send proposals to clients through a secure portal where they can review, approve, or request changes — no PDF email chains.",
    benefits: [
      "Branded review link with 30-day expiration",
      "Clients can approve or reject in one click",
      "Capture client feedback in writing",
      "Track which proposals have been viewed",
    ],
  },
  "api-access": {
    requiredTier: "enterprise",
    title: "API Access",
    planLabel: "Enterprise",
    description:
      "Programmatically create proposals, fetch results, and integrate Sentinel Forge with your CRM, dispatch, or internal tools.",
    benefits: [
      "REST endpoints for create/list/get",
      "Hashed API keys with rotation",
      "Webhook support for proposal events",
      "Built for high-volume integrations",
    ],
  },
  "white-labeling": {
    requiredTier: "enterprise",
    title: "White-Label Branding",
    planLabel: "Enterprise",
    description:
      "Replace Sentinel Forge branding with your own — your logo, colors, and footer text on every proposal and the app shell.",
    benefits: [
      "Your company name in the app header",
      "Custom accent color on proposals",
      "Custom footer text & legal lines",
      "Hide all Sentinel Forge branding",
    ],
  },
};

/**
 * Centralized feature-access logic. Returns:
 *  - hasAccess: whether the current user can use the feature
 *  - meta: copy used by upgrade prompts
 *  - isLoading: while subscription is resolving (avoid flashing locked state)
 */
export function useFeatureAccess(feature: FeatureKey) {
  const { subscription, isDemoUser } = useAuth();
  const meta = FEATURE_META[feature];

  const tier = subscription.tier;
  const subscribedAndActive = subscription.subscribed;

  // Demo users get the agency-tier preview by default but should still be
  // able to test the upgrade flow on enterprise-only features.
  const tierRank: Record<string, number> = {
    starter: 1,
    professional: 2,
    agency: 3,
    enterprise: 4,
    testing: 4,
  };
  const requiredRank = tierRank[meta.requiredTier];
  const currentRank = tier ? tierRank[tier] ?? 0 : 0;

  const hasAccess =
    isDemoUser
      ? currentRank >= requiredRank
      : subscribedAndActive && currentRank >= requiredRank;

  return {
    hasAccess,
    meta,
    isLoading: subscription.isLoading,
  };
}
